"use client";

import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { ApiPlan, createApiPortalSession, createOrUpgradeApiSubscription, getApiPlans } from '@/services/apiPlansService';
import { colors, typography } from '@/theme/colors';
import { logError } from '@/utils/appInsights';
import ApiPlanCard from './ApiPlanCard';
interface ApiPlansSectionProps {
  currentPlanSlug: string | null;
  storeId: number;
  onUpgradeComplete?: () => void;
}
const ApiPlansSection: React.FC<ApiPlansSectionProps> = ({
  currentPlanSlug,
  storeId,
  onUpgradeComplete
}) => {
  const t = useTranslations('apiPlans');
  const {
    enqueueSnackbar
  } = useSnackbar();
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [upgradingSlug, setUpgradingSlug] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getApiPlans();
      setPlans(data);
    } catch (error) {
      logError(error, {
        context: 'fetchApiPlans'
      });
      enqueueSnackbar(t('errorLoadingPlans'), {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, t]);
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);
  const handleUpgrade = async (planSlug: string) => {
    setUpgradingSlug(planSlug);
    try {
      const response = await createOrUpgradeApiSubscription({
        planSlug,
        storeId,
        name: `${planSlug} subscription`
      });
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else if (response.apiKey) {
        enqueueSnackbar(t('freeKeyCreated'), {
          variant: 'success'
        });
        onUpgradeComplete?.();
      }
    } catch (error) {
      logError(error, {
        context: 'upgradeApiSubscription',
        planSlug
      });
      enqueueSnackbar(t('upgradeError'), {
        variant: 'error'
      });
    } finally {
      setUpgradingSlug(null);
    }
  };
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const portalUrl = await createApiPortalSession();
      window.location.href = portalUrl;
    } catch (error) {
      logError(error, {
        context: 'openBillingPortal'
      });
      enqueueSnackbar(t('portalError'), {
        variant: 'error'
      });
    } finally {
      setPortalLoading(false);
    }
  };
  const hasPaidPlan = currentPlanSlug ? currentPlanSlug !== 'api-free' : false;
  return <Box component="section" aria-labelledby="upgrade-section-title" id="upgrade-section" sx={{
    bgcolor: colors.background.light,
    border: `1px solid ${colors.border.light}`,
    borderRadius: '12px',
    p: 3,
    mb: 3
  }} data-sentry-element="Box" data-sentry-component="ApiPlansSection" data-sentry-source-file="ApiPlansSection.tsx">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      mb: 3,
      flexWrap: 'wrap',
      gap: 2
    }} data-sentry-element="Box" data-sentry-source-file="ApiPlansSection.tsx">
        <Box data-sentry-element="Box" data-sentry-source-file="ApiPlansSection.tsx">
          <Typography id="upgrade-section-title" sx={{
          ...typography.presets.sectionTitle,
          color: colors.text.primary
        }} data-sentry-element="Typography" data-sentry-source-file="ApiPlansSection.tsx">
            {t('sectionTitle')}
          </Typography>
          <Typography sx={{
          ...typography.presets.bodySecondary,
          color: colors.text.tertiary,
          mt: 0.5
        }} data-sentry-element="Typography" data-sentry-source-file="ApiPlansSection.tsx">
            {t('sectionSubtitle')}
          </Typography>
        </Box>

        {hasPaidPlan && <Button variant="outlined" startIcon={<ManageAccountsIcon />} onClick={handleManageSubscription} disabled={portalLoading} sx={{
        textTransform: 'none',
        minHeight: 44,
        borderColor: colors.border.medium,
        color: colors.text.secondary,
        '&:hover': {
          borderColor: colors.primary.main,
          color: colors.primary.main
        }
      }}>
            {portalLoading ? t('loading') : t('manageSubscription')}
          </Button>}
      </Box>

      {isLoading ? <Box sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        lg: 'repeat(4, 1fr)'
      },
      gap: 2
    }}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rectangular" height={280} sx={{
        borderRadius: '12px'
      }} />)}
        </Box> : <Box role="list" sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        lg: 'repeat(4, 1fr)'
      },
      gap: 2
    }}>
          {plans.map((plan, index) => <ApiPlanCard key={plan.id} plan={plan} isCurrentPlan={currentPlanSlug === plan.slug} isPopular={plan.slug === 'api-pro'} onUpgrade={handleUpgrade} loading={upgradingSlug === plan.slug} disabled={upgradingSlug !== null && upgradingSlug !== plan.slug} index={index} currentPlanSlug={currentPlanSlug} />)}
        </Box>}
    </Box>;
};
export default ApiPlansSection;